import { Component, OnInit } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { TinyColor } from '@ctrl/tinycolor';
import { ColorFormat } from 'src/app/components/color-picker/interfaces';

interface ColorSquare {
  hex: string;
  rgba: string;
  label: string;
}

@Component({
  selector: 'app-cores',
  templateUrl: './cores.component.html',
  styleUrls: ['./cores.component.css']
})
export class CoresComponent implements OnInit {
  colors: ColorSquare[] = [];
  private readonly COOKIE_KEY = 'app-color-picker';
  private readonly COOKIE_EXPIRY_DAYS = 365;
  activeColorIndex: number | null = null;
  activeColorValue = '#FFFFFFFF';
  activeColorFormat: ColorFormat = 'hex';

  constructor(private cookieService: CookieService) { }

  ngOnInit(): void {
    for (let i = 0; i < 9; i++) {
      this.colors.push({
        hex: '',
        rgba: 'transparent',
        label: ''
      });
    }

    this.loadColorsFromCookies();
  }

  convertHexToRgba(hex: string): string {
    if (hex.length !== 8) {
      return 'transparent';
    }

    try {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const a = parseInt(hex.substring(6, 8), 16);

      if (isNaN(r) || isNaN(g) || isNaN(b) || isNaN(a)) {
        return 'transparent';
      }

      const opacity = (a / 255).toFixed(2);

      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch {
      return 'transparent';
    }
  }

  onHexInput(index: number, event: any): void {
    let value = event.target.value.toUpperCase();
    
    value = value.replace(/[^0-9A-F]/g, '');
    value = value.substring(0, 8);
    this.colors[index].hex = value;
    
    if (value.length === 6 || value.length === 8) {
      const hexForConversion = value.length === 6 ? value + 'FF' : value;
      this.colors[index].rgba = this.convertHexToRgba(hexForConversion);
    } else {
      this.colors[index].rgba = 'transparent';
    }
    
    this.saveColorsToCookies();
  }

  onHexBlur(index: number): void {
    const color = this.colors[index];
    
    if (color.hex.length === 6) {
      color.hex = color.hex + 'FF';
      color.rgba = this.convertHexToRgba(color.hex);
    } 
    else if (color.hex.length !== 8 && color.hex.length > 0) {
      color.hex = '';
      color.rgba = 'transparent';
    }
    
    this.saveColorsToCookies();
  }

  onLabelInput(index: number, event: any): void {
    this.colors[index].label = event.target.value;
    this.saveColorsToCookies();
  }

  copyToClipboard(index: number): void {
    const color = this.colors[index];

    if (!color.hex || color.hex.length === 0) {
      return;
    }

    const hexToCopy = color.hex.length === 6 ? color.hex + 'FF' : color.hex;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(hexToCopy).catch((err) => {
        console.error('Erro ao copiar para a área de transferência:', err);
      });
      return;
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = hexToCopy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);

      textarea.select();
      document.execCommand('copy');

      document.body.removeChild(textarea);
    } catch (err) {
      console.error('Erro ao copiar para a área de transferência:', err);
    }
  }

  clearColor(index: number): void {
    this.colors[index].hex = '';
    this.colors[index].rgba = 'transparent';
    this.colors[index].label = '';
    
    this.saveColorsToCookies();
  }

  openColorPicker(index: number): void {
    this.activeColorIndex = index;
    this.activeColorValue = this.toPickerValue(this.colors[index].hex);
    this.activeColorFormat = 'hex';
  }

  onActiveColorChange(value: string): void {
    if (this.activeColorIndex == null) {
      return;
    }

    const hexValue = this.toHexValue(value);
    this.colors[this.activeColorIndex].hex = hexValue;
    this.colors[this.activeColorIndex].rgba = this.convertHexToRgba(hexValue);
    this.saveColorsToCookies();
  }

  private hexToColorPickerFormat(hex: string): string {
    if (hex.length !== 8) {
      return '#ffffff';
    }
    return '#' + hex.substring(0, 6);
  }

  getColorPickerValue(hex: string): string {
    return this.hexToColorPickerFormat(hex);
  }

  onColorPickerChange(index: number, event: any): void {
    const hexColor = event.target.value;
    
    if (!hexColor || hexColor.length !== 7) {
      return;
    }

    const rgbHex = hexColor.substring(1).toUpperCase();
    const hexValue = rgbHex + 'FF';

    this.colors[index].hex = hexValue;
    this.colors[index].rgba = this.convertHexToRgba(hexValue);

    this.saveColorsToCookies();
  }

  private saveColorsToCookies(): void {
    const colorData = this.colors.map(color => ({
      hex: color.hex,
      label: color.label
    }));
    
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.COOKIE_EXPIRY_DAYS);
    
    this.cookieService.set(
      this.COOKIE_KEY,
      JSON.stringify(colorData),
      expiryDate,
      '/'
    );
  }

  private toPickerValue(hex: string): string {
    if (!hex) {
      return '#FFFFFFFF';
    }

    const clean = hex.replace('#', '').trim();
    if (clean.length === 6) {
      return `#${clean}FF`;
    }

    if (clean.length === 8) {
      return `#${clean}`;
    }

    const fallback = new TinyColor(hex);
    return fallback.isValid ? fallback.toHex8String() : '#FFFFFFFF';
  }

  private toHexValue(value: string): string {
    const color = new TinyColor(value);
    if (!color.isValid) {
      return 'FFFFFFFF';
    }

    return color.toHex8().toUpperCase();
  }

  private loadColorsFromCookies(): void {
    if (this.cookieService.check(this.COOKIE_KEY)) {
      try {
        const colorData = JSON.parse(this.cookieService.get(this.COOKIE_KEY));
        
        if (Array.isArray(colorData) && colorData.length === 9) {
          colorData.forEach((item, index) => {
            if (item.hex && item.hex.length === 8) {
              this.colors[index].hex = item.hex;
              this.colors[index].rgba = this.convertHexToRgba(item.hex);
            }
            if (item.label) {
              this.colors[index].label = item.label;
            }
          });
        }
      } catch (error) {
        console.error('Erro ao carregar cores dos cookies:', error);
      }
    }
  }
}
